import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useLanguage } from '../contexts/LanguageContext';
import { shareWeeklyImage } from '../lib/shareWeeklyImage';
import { toast } from 'sonner';
import {
    TrendingUp, TrendingDown, Minus, Dumbbell, Moon, Droplets, Flame,
    Calendar, RefreshCw, Share2, ChevronRight, Sparkles, Ticket, Trophy,
    Coffee, Wine, Beef, Smile, Utensils, Check, Coins
} from 'lucide-react';

const NANOUK_STATE_IMG = {
    fit: '/nanouk/energy_fit.png',
    balanced: '/nanouk/energy_balanced.png',
    light: '/nanouk/energy_light.png',
    heavy: '/nanouk/energy_heavy.png',
    crushed: '/nanouk/energy_crushed.png',
};

function TalerWeekChart({ taler, language }) {
    const de = language === 'de';
    const { budget, series, avg_net, week_balance, tracked_days, nanouk_state } = taler;
    const maxVal = Math.max(budget, ...series.map(d => d.net), 1) * 1.15;
    const budgetPct = (budget / maxVal) * 100;
    const dayLabel = (dateStr) => new Date(`${dateStr}T12:00:00Z`).toLocaleDateString(de ? 'de-CH' : 'en-GB', { weekday: 'short' }).slice(0, 2);

    const stateText = {
        fit: de ? 'Nanouk rennt federleicht!' : 'Nanouk is running light!',
        balanced: de ? 'Nanouk ist gut im Gleichgewicht.' : 'Nanouk is well balanced.',
        light: de ? 'Nanouk trägt einen kleinen Butter-Rucksack.' : 'Nanouk carries a small butter backpack.',
        heavy: de ? 'Nanouk schleppt schwere Säcke…' : 'Nanouk is hauling heavy sacks…',
        crushed: de ? 'Nanouk liegt unterm Butterberg!' : 'Nanouk is buried under butter!',
    }[nanouk_state];

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <Card className="border-amber-200 dark:border-amber-800/30 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20" data-testid="weekly-taler-card">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                            <Coins className="w-4 h-4 text-white" />
                        </div>
                        {de ? 'Taler-Bilanz' : 'Taler balance'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center gap-3">
                        <img src={NANOUK_STATE_IMG[nanouk_state]} alt="Nanouk" className="w-16 h-16 object-contain shrink-0" data-testid={`weekly-taler-nanouk-${nanouk_state}`} />
                        <div>
                            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="weekly-taler-avg">
                                Ø {avg_net} <span className="text-sm font-medium text-muted-foreground">/ {budget} {de ? 'Taler' : 'Taler'}</span>
                            </p>
                            <p className="text-xs text-muted-foreground">{stateText}</p>
                            {tracked_days > 0 && (
                                <p className={`text-xs font-semibold ${week_balance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="weekly-taler-balance">
                                    {week_balance >= 0
                                        ? (de ? `+${week_balance} Taler gespart diese Woche` : `+${week_balance} Taler saved this week`)
                                        : (de ? `${week_balance} Taler über Budget diese Woche` : `${week_balance} Taler over budget this week`)}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="relative h-28 flex items-end gap-1.5 pt-2" data-testid="weekly-taler-chart">
                        <div className="absolute left-0 right-0 border-t-2 border-dashed border-amber-500/60 z-0" style={{ bottom: `${budgetPct}%` }}>
                            <span className="absolute -top-4 right-0 text-[9px] text-amber-700 font-medium">{de ? 'Budget' : 'Budget'} {budget}</span>
                        </div>
                        {series.map((d) => {
                            const hPct = d.net > 0 ? Math.max((d.net / maxVal) * 100, 4) : 0;
                            const overB = d.net > budget;
                            return (
                                <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full z-10">
                                    {d.spent > 0 && <span className={`text-[9px] font-semibold ${overB ? 'text-red-600' : 'text-amber-700'}`}>{d.net}</span>}
                                    <div
                                        className={`w-full rounded-t-md transition-all ${d.spent === 0 ? 'bg-muted h-1' : overB ? 'bg-red-400' : 'bg-amber-400'}`}
                                        style={d.spent > 0 ? { height: `${hPct}%` } : undefined}
                                    />
                                    <span className="text-[9px] text-muted-foreground mt-1">{dayLabel(d.date)}</span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
    moon: Moon,
    droplets: Droplets,
    dumbbell: Dumbbell,
    coffee: Coffee,
    wine: Wine,
    beef: Beef,
    smile: Smile,
    trophy: Trophy,
    'trending-up': TrendingUp,
    utensils: Utensils,
};

export function WeeklyReport({ onClose }) {
    const { language } = useLanguage();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [acceptingChallenge, setAcceptingChallenge] = useState(false);
    const [challengeAccepted, setChallengeAccepted] = useState(false);
    const [sharing, setSharing] = useState(false);

    const handleShare = async () => {
        if (!report) return;
        setSharing(true);
        try {
            const outcome = await shareWeeklyImage(report, language);
            if (outcome === 'downloaded') {
                toast.success(language === 'de' ? 'Bild heruntergeladen 📸' : 'Image downloaded 📸');
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                toast.error(language === 'de' ? 'Teilen fehlgeschlagen' : 'Sharing failed');
            }
        } finally {
            setSharing(false);
        }
    };

    const t = {
        de: {
            title: 'Wochenrückblick',
            subtitle: 'Deine Fortschritte der letzten 7 Tage',
            workouts: 'Workouts',
            sleep: 'Schlaf',
            water: 'Wasser',
            calories: 'Kalorien',
            thisWeek: 'Diese Woche',
            lastWeek: 'Letzte Woche',
            change: 'Veränderung',
            duration: 'Dauer',
            burned: 'verbrannt',
            quality: 'Qualität',
            avgPerNight: 'Ø pro Nacht',
            dailyAvg: 'Tages-Ø',
            meals: 'Mahlzeiten',
            protein: 'Protein',
            liters: 'Liter',
            hours: 'Stunden',
            minutes: 'Minuten',
            noData: 'Noch keine Daten für diese Woche',
            refresh: 'Aktualisieren',
            share: 'Teilen',
            close: 'Schließen',
            improvements: 'Verbesserungen',
            needsAttention: 'Braucht Aufmerksamkeit',
            goldenTicket: 'Nanouks Goldenes Ticket',
            goldenTicketSubtitle: 'Dein Fokus für nächste Woche',
            acceptChallenge: 'Challenge angenommen!',
            challengeAccepted: 'Du bist dabei!',
            rewardPoints: 'Bonus-Punkte',
            discoveries: 'Top Entdeckungen',
            goal: 'Ziel'
        },
        en: {
            title: 'Weekly Review',
            subtitle: 'Your progress over the last 7 days',
            workouts: 'Workouts',
            sleep: 'Sleep',
            water: 'Water',
            calories: 'Calories',
            thisWeek: 'This week',
            lastWeek: 'Last week',
            change: 'Change',
            duration: 'Duration',
            burned: 'burned',
            quality: 'Quality',
            avgPerNight: 'Avg per night',
            dailyAvg: 'Daily avg',
            meals: 'Meals',
            protein: 'Protein',
            liters: 'Liters',
            hours: 'hours',
            minutes: 'minutes',
            noData: 'No data for this week yet',
            refresh: 'Refresh',
            share: 'Share',
            close: 'Close',
            improvements: 'Improvements',
            needsAttention: 'Needs attention',
            goldenTicket: "Nanouk's Golden Ticket",
            goldenTicketSubtitle: 'Your focus for next week',
            acceptChallenge: 'Challenge accepted!',
            challengeAccepted: "You're in!",
            rewardPoints: 'Bonus points',
            discoveries: 'Top Discoveries',
            goal: 'Goal'
        }
    }[language];

    const loadReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API}/api/reports/weekly?language=${language}`);
            setReport(response.data);
            
            // Check if challenge already accepted
            const challengeRes = await axios.get(`${API}/api/reports/challenge/active`);
            if (challengeRes.data.active_challenge) {
                setChallengeAccepted(true);
            }
        } catch (err) {
            console.error('Failed to load weekly report:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptChallenge = async () => {
        if (!report?.golden_ticket) return;
        
        setAcceptingChallenge(true);
        try {
            await axios.post(`${API}/api/reports/challenge/accept`, {
                challenge_id: report.golden_ticket.id,
                goal: report.golden_ticket.goal,
                reward_points: report.golden_ticket.reward_points
            });
            
            setChallengeAccepted(true);
            toast.success(language === 'de' ? 'Challenge angenommen! +10 Punkte 🎫' : 'Challenge accepted! +10 points 🎫');
        } catch (err) {
            console.error('Failed to accept challenge:', err);
            toast.error(language === 'de' ? 'Fehler beim Annehmen' : 'Failed to accept');
        } finally {
            setAcceptingChallenge(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const getTrendIcon = (changePercent) => {
        if (changePercent > 5) return <TrendingUp className="w-4 h-4 text-green-500" />;
        if (changePercent < -5) return <TrendingDown className="w-4 h-4 text-red-500" />;
        return <Minus className="w-4 h-4 text-gray-400" />;
    };

    const getTrendColor = (changePercent, inverse = false) => {
        const isPositive = inverse ? changePercent < 0 : changePercent > 0;
        if (Math.abs(changePercent) < 5) return 'text-gray-500';
        return isPositive ? 'text-green-500' : 'text-red-500';
    };

    const formatChange = (changePercent) => {
        if (Math.abs(changePercent) > 200) {
            return changePercent > 0 
                ? (language === 'de' ? 'Neu' : 'New')
                : (language === 'de' ? 'Pause' : 'Gap');
        }
        if (Math.abs(changePercent) < 5) return language === 'de' ? 'stabil' : 'stable';
        if (changePercent > 0) return `+${Math.round(changePercent)}%`;
        return `${Math.round(changePercent)}%`;
    };

    if (loading) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton className="h-32 w-full" />
                <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={loadReport}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t.refresh}
                </Button>
            </div>
        );
    }

    if (!report) return null;

    const { stats, nanouk, week_range } = report;

    return (
        <div className="space-y-6" data-testid="weekly-report">
            {/* Nanouk Message Card */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600 p-6 text-foreground"
            >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative flex items-start gap-4">
                    <motion.img
                        src={nanouk.image}
                        alt="Nanouk"
                        className="w-20 h-20 rounded-full bg-white/20 p-1"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 opacity-70" />
                            <span className="text-sm opacity-70">
                                {week_range.start} - {week_range.end}
                            </span>
                        </div>
                        <p className="text-lg font-medium leading-relaxed">
                            {nanouk.message}
                        </p>
                        
                        {/* Badges for improvements/concerns */}
                        <div className="flex flex-wrap gap-2 mt-3">
                            {nanouk.improvements.map((item) => (
                                <Badge key={item} className="bg-green-500/30 text-foreground border-green-400/50">
                                    <TrendingUp className="w-3 h-3 mr-1" />
                                    {item === 'workouts' && t.workouts}
                                    {item === 'sleep' && t.sleep}
                                    {item === 'water' && t.water}
                                    {item === 'nutrition' && t.calories}
                                </Badge>
                            ))}
                            {nanouk.concerns.map((item) => (
                                <Badge key={item} className="bg-orange-500/30 text-foreground border-orange-400/50">
                                    <TrendingDown className="w-3 h-3 mr-1" />
                                    {item === 'workouts' && t.workouts}
                                    {item === 'sleep' && t.sleep}
                                    {item === 'water' && t.water}
                                    {item === 'nutrition' && t.calories}
                                </Badge>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
                {/* Workouts Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="h-full border-orange-200 dark:border-orange-800/30 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                                    <Dumbbell className="w-4 h-4 text-foreground" />
                                </div>
                                {t.workouts}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                                    {stats.workouts.this_week}
                                </span>
                                <div className="flex items-center gap-1">
                                    {getTrendIcon(stats.workouts.change_percent)}
                                    <span className={`text-sm font-medium ${getTrendColor(stats.workouts.change_percent)}`}>
                                        {formatChange(stats.workouts.change_percent)}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                                <div className="flex justify-between">
                                    <span>{t.duration}:</span>
                                    <span>{stats.workouts.total_duration_min} {t.minutes}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>{t.burned}:</span>
                                    <span>{stats.workouts.calories_burned} kcal</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Sleep Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card className="h-full border-indigo-200 dark:border-indigo-800/30 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 dark:to-violet-950/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                                    <Moon className="w-4 h-4 text-foreground" />
                                </div>
                                {t.sleep}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                                    {stats.sleep.avg_per_night}h
                                </span>
                                <div className="flex items-center gap-1">
                                    {getTrendIcon(stats.sleep.change_percent)}
                                    <span className={`text-sm font-medium ${getTrendColor(stats.sleep.change_percent)}`}>
                                        {formatChange(stats.sleep.change_percent)}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                                <div className="flex justify-between">
                                    <span>{language === 'de' ? 'Qualitaet:' : 'Quality:'}</span>
                                    <span>{stats.sleep.avg_quality}/10</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>{language === 'de' ? 'Naechte:' : 'Nights:'}</span>
                                    <span>{stats.sleep.days_logged}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Water Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                >
                    <Card className="h-full border-cyan-200 dark:border-cyan-800/30 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center">
                                    <Droplets className="w-4 h-4 text-foreground" />
                                </div>
                                {t.water}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-bold text-cyan-600 dark:text-primary">
                                    {(stats.water.daily_avg_ml / 1000).toFixed(1)}L
                                </span>
                                <div className="flex items-center gap-1">
                                    {getTrendIcon(stats.water.change_percent)}
                                    <span className={`text-sm font-medium ${getTrendColor(stats.water.change_percent)}`}>
                                        {formatChange(stats.water.change_percent)}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                <div className="flex justify-between">
                                    <span>{language === 'de' ? 'Gesamt:' : 'Total:'}</span>
                                    <span>{(stats.water.this_week / 1000).toFixed(1)}L</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Calories Card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <Card className="h-full border-green-200 dark:border-green-800/30 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                                    <Flame className="w-4 h-4 text-foreground" />
                                </div>
                                {t.calories}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex items-end justify-between">
                                <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                                    {stats.calories.daily_avg.toLocaleString()}
                                </span>
                                <div className="flex items-center gap-1">
                                    {getTrendIcon(stats.calories.change_percent)}
                                    <span className={`text-sm font-medium ${getTrendColor(stats.calories.change_percent, true)}`}>
                                        {formatChange(stats.calories.change_percent)}
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                                <div className="flex justify-between">
                                    <span>{language === 'de' ? 'Gesamt:' : 'Total:'}</span>
                                    <span>{stats.calories.this_week.toLocaleString()} kcal</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>{t.meals}:</span>
                                    <span>{stats.calories.meals_logged}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Taler weekly balance with Nanouk fitness state */}
            {report.taler?.enabled && <TalerWeekChart taler={report.taler} language={language} />}

            {/* Top Discoveries */}
            {report.discoveries && report.discoveries.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-2"
                >
                    <h3 className="font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        {t.discoveries}
                    </h3>
                    <div className="space-y-2">
                        {report.discoveries.map((discovery, idx) => {
                            const DiscoveryIcon = ICON_MAP[discovery.icon] || Sparkles;
                            return (
                                <div 
                                    key={idx}
                                    className={`flex items-start gap-3 p-3 rounded-lg ${
                                        discovery.type === 'success' 
                                            ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800' 
                                            : discovery.type === 'warning'
                                            ? 'bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800'
                                            : 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800'
                                    }`}
                                >
                                    <DiscoveryIcon className={`w-5 h-5 mt-0.5 ${
                                        discovery.type === 'success' ? 'text-green-600' 
                                        : discovery.type === 'warning' ? 'text-orange-600' 
                                        : 'text-blue-600'
                                    }`} />
                                    <p className="text-sm">{discovery.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            )}

            {/* Golden Ticket */}
            {report.golden_ticket && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 }}
                    className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 p-1"
                >
                    <div className="relative bg-white dark:bg-card rounded-xl p-4">
                        {/* Decorative ticket holes */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-amber-400 rounded-full" />
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 bg-amber-400 rounded-full" />
                        
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                                <Ticket className="w-6 h-6 text-foreground" />
                            </div>
                            <div>
                                <h3 className="font-bold text-amber-900 dark:text-amber-100">{t.goldenTicket}</h3>
                                <p className="text-xs text-amber-700 dark:text-amber-300">{t.goldenTicketSubtitle}</p>
                            </div>
                        </div>
                        
                        {/* Challenge Title */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                {(() => {
                                    const TicketIcon = ICON_MAP[report.golden_ticket.icon] || Sparkles;
                                    return <TicketIcon className="w-5 h-5 text-amber-600" />;
                                })()}
                                <span className="font-semibold text-lg">{report.golden_ticket.title}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {report.golden_ticket.challenge}
                            </p>
                        </div>
                        
                        {/* Goal & Reward */}
                        <div className="flex items-center justify-between mb-4 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                            <div>
                                <p className="text-xs text-amber-700 dark:text-amber-300">{t.goal}</p>
                                <p className="font-medium text-sm">{report.golden_ticket.goal}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-amber-700 dark:text-amber-300">{t.rewardPoints}</p>
                                <p className="font-bold text-amber-600">+{report.golden_ticket.reward_points} 🏆</p>
                            </div>
                        </div>
                        
                        {/* Accept Button */}
                        {challengeAccepted ? (
                            <div className="flex items-center justify-center gap-2 py-3 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-700 dark:text-green-300">
                                <Check className="w-5 h-5" />
                                <span className="font-medium">{t.challengeAccepted}</span>
                            </div>
                        ) : (
                            <Button
                                onClick={handleAcceptChallenge}
                                disabled={acceptingChallenge}
                                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-foreground font-semibold"
                                data-testid="accept-challenge-btn"
                            >
                                {acceptingChallenge ? (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity }}
                                    >
                                        <Sparkles className="w-5 h-5" />
                                    </motion.div>
                                ) : (
                                    <>
                                        <Ticket className="w-5 h-5 mr-2" />
                                        {t.acceptChallenge}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Action Buttons */}
            <Button
                onClick={handleShare}
                disabled={sharing}
                className="w-full h-12 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white font-semibold"
                data-testid="weekly-share-btn"
            >
                <Share2 className="w-5 h-5 mr-2" />
                {sharing
                    ? (language === 'de' ? 'Bild wird erstellt…' : 'Creating image…')
                    : (language === 'de' ? 'Als Bild teilen' : 'Share as image')}
            </Button>
            <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={loadReport}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t.refresh}
                </Button>
                {onClose && (
                    <Button variant="outline" className="flex-1" onClick={onClose}>
                        {t.close}
                    </Button>
                )}
            </div>
        </div>
    );
}
